import { supabase as supabaseClient } from '../../lib/supabaseClient';

export interface SignedDocuments {
  cremationContract: string | null;
  sp3File: string | null;
}

const getAttachmentUrl = async (path: string): Promise<string | null> => {
  try {
    const { data, error } = await supabaseClient.storage
      .from('attachments')
      .createSignedUrl(path, 300);
    if (error) {
      console.error('Error generating signed URL for attachment:', error);
      return null;
    }
    return data.signedUrl;
  } catch (error) {
    console.error('Error generating signed URL for attachment:', error);
    return null;
  }
};

/**
 * Resolve short-lived signed URLs for the case's SP3 and latest signed
 * cremation agreement (funeral_documents first, legacy attachments as SP3
 * fallback). Used to attach documents to dispatch notification emails.
 */
export const fetchSignedDocuments = async (
  funeralId: string | number | undefined,
): Promise<SignedDocuments> => {
  if (!funeralId) return { cremationContract: null, sp3File: null };

  try {
    let sp3Url: string | null = null;
    try {
      const { data: funeralDocs, error: fdError } = await supabaseClient
        .from('funeral_documents')
        .select('id, storage_bucket, storage_path, document_type')
        .eq('funeral_id', funeralId)
        .eq('document_type', 'sp3')
        .order('received_at', { ascending: false })
        .limit(1);

      if (!fdError && funeralDocs && funeralDocs.length > 0) {
        const sp3Doc = funeralDocs[0];
        const { data: signedData, error: signError } = await supabaseClient.storage
          .from(sp3Doc.storage_bucket)
          .createSignedUrl(sp3Doc.storage_path, 300);
        if (signError) {
          console.error(
            `Failed to create signed URL for SP3 (bucket=${sp3Doc.storage_bucket}, path=${sp3Doc.storage_path}):`,
            signError,
          );
        }
        if (signedData?.signedUrl) {
          sp3Url = signedData.signedUrl;
        }
      }
    } catch (e) {
      console.error('Error fetching SP3 from funeral_documents:', e);
    }

    if (!sp3Url) {
      const { data: attachments, error: attachmentsError } = await supabaseClient
        .from('attachments')
        .select('*')
        .eq('funeral_id', funeralId)
        .order('created_at', { ascending: false });

      if (attachmentsError) {
        console.error('Error fetching attachments:', attachmentsError);
      }

      const sp3File = attachments?.find(
        (attachment: { document_type?: string; title?: string }) =>
          attachment.document_type === 'SP3' ||
          (attachment.title && attachment.title.toLowerCase().includes('sp3')),
      );
      if (sp3File) {
        sp3Url = await getAttachmentUrl(sp3File.path);
      }
    }

    // Latest signed cremation agreement from funeral_documents (the array on
    // funerals.contracts_signed_urls accumulates every submission and is not
    // reliable when a case has multiple contracts).
    let cremationContract: string | null = null;
    try {
      const { data: cremationDocs, error: cremationErr } = await supabaseClient
        .from('funeral_documents')
        .select('storage_bucket, storage_path, public_url')
        .eq('funeral_id', funeralId)
        .eq('document_type', 'cremation_agreement')
        .order('received_at', { ascending: false })
        .limit(1);

      if (cremationErr) {
        console.error('Error fetching cremation agreement:', cremationErr);
      } else if (cremationDocs && cremationDocs.length > 0) {
        const doc = cremationDocs[0];
        const { data: signedData, error: signError } = await supabaseClient.storage
          .from(doc.storage_bucket)
          .createSignedUrl(doc.storage_path, 3600);
        if (signError) {
          console.error(
            `Failed to sign cremation agreement URL (bucket=${doc.storage_bucket}, path=${doc.storage_path}):`,
            signError,
          );
          cremationContract = doc.public_url || null;
        } else {
          cremationContract = signedData?.signedUrl || doc.public_url || null;
        }
      }
    } catch (cremationCatch) {
      console.error('Error resolving cremation agreement:', cremationCatch);
    }

    return { cremationContract, sp3File: sp3Url };
  } catch (error) {
    console.error('Error fetching signed documents:', error);
    return { cremationContract: null, sp3File: null };
  }
};

export interface CaseDocument {
  url: string;
  receivedAt: string | null;
}

/**
 * Short-lived signed URLs for the latest non-archived document of each type
 * (funeral_documents; the SP3 falls back to the legacy attachments copy, in the
 * same order as fetchSignedDocuments). Missing types map to null.
 */
export const fetchCaseDocuments = async (
  funeralId: string | number,
  types: string[],
): Promise<Record<string, CaseDocument | null>> => {
  const out: Record<string, CaseDocument | null> = {};
  types.forEach((t) => {
    out[t] = null;
  });
  if (!funeralId || types.length === 0) return out;

  const { data, error } = await supabaseClient
    .from('funeral_documents')
    .select('document_type, storage_bucket, storage_path, public_url, received_at')
    .eq('funeral_id', funeralId)
    .is('archived_at', null)
    .in('document_type', types)
    .order('received_at', { ascending: false });
  if (error) console.error('Error fetching case documents:', error);

  for (const t of types) {
    const doc = (data || []).find((d: { document_type: string }) => d.document_type === t);
    if (!doc) continue;
    const { data: signed, error: signError } = await supabaseClient.storage
      .from(doc.storage_bucket)
      .createSignedUrl(doc.storage_path, 3600);
    if (signError) {
      console.error(`Failed to sign ${t} (bucket=${doc.storage_bucket}, path=${doc.storage_path}):`, signError);
    }
    const url = signed?.signedUrl || doc.public_url || null;
    if (url) out[t] = { url, receivedAt: doc.received_at ?? null };
  }

  if (types.includes('sp3') && !out.sp3) {
    const legacy = await fetchSignedDocuments(funeralId);
    if (legacy.sp3File) out.sp3 = { url: legacy.sp3File, receivedAt: null };
  }
  return out;
};

/** Which of `types` have a non-archived document on the case (for status chips; no URL signing). */
export const fetchOnFileTypes = async (
  funeralId: string | number,
  types: string[],
): Promise<Record<string, { receivedAt: string | null }>> => {
  const out: Record<string, { receivedAt: string | null }> = {};
  if (!funeralId || types.length === 0) return out;
  const { data } = await supabaseClient
    .from('funeral_documents')
    .select('document_type, received_at')
    .eq('funeral_id', funeralId)
    .is('archived_at', null)
    .in('document_type', types)
    .order('received_at', { ascending: false });
  for (const d of (data || []) as Array<{ document_type: string; received_at: string | null }>) {
    if (!out[d.document_type]) out[d.document_type] = { receivedAt: d.received_at };
  }
  if (types.includes('sp3') && !out.sp3) {
    const { data: att } = await supabaseClient
      .from('attachments')
      .select('document_type, title, created_at')
      .eq('funeral_id', funeralId);
    const legacy = (att || []).find(
      (a: { document_type?: string; title?: string }) =>
        a.document_type === 'SP3' || (a.title && a.title.toLowerCase().includes('sp3')),
    );
    if (legacy) out.sp3 = { receivedAt: legacy.created_at ?? null };
  }
  return out;
};
